import { useEffect } from "react";

export const Config = {
  Providers: {
    OpenSearch: 'opensearch'
  },
  Layouts: {
    Standard: 'standard',
    Compact: 'compact',
    Horizontal: 'horizontal',
  },
  ViewModes: {
    Card: 'card',
    Accordion: 'accordion',
  },
  FacetWidgets: {
    // Default (empty widget value) renders an inline checkbox list — see facetWidgetOptions.
    Multiselect: 'multiselect',    // dropdown panel of checkboxes (multi-select)
    Dropdown: 'dropdown',          // native <select> (single-select)
  },
  Mapping: {
    Card: {
      Featured: 'featured',
      Badges: 'badges',
      Pretitle: 'pretitle',
      Title: 'title',
      Subtitle: 'subtitle',
      ImageURL: 'imageURL',
      Body: 'body',
      Links: 'links',
    },
    Accordion: {
      ImageURL: 'imageURL',
      Title: 'title',
      Badge: 'badge',
      Body: 'body',
      Markdown: 'markdown',
      DescriptionList: 'descriptionList',
      DescriptionListLabels: 'descriptionListLabels',
      Links: 'links',
    }
  },
  DisplayOptions: {
    ItemsPerPage: {
      DefaultValue: 12,
      DefaultOptions: [12, 24, 36],
    }
  },
  I18n: {
    Filters: {
      BlockLabel: I18n.t('open_search.filters.block_label'),
      SortByLabel: I18n.t('open_search.filters.sort_by_label'),
      ClearAll: I18n.t('open_search.filters.clear_all'),
      RemoveFilter: (field, value) => I18n.t('open_search.filters.remove_filter', {field: field, value: value}),
    },
    Pager: {
      FirstPage: I18n.t('open_search.pagination.first_page'),
      LastPage: I18n.t('open_search.pagination.last_page'),
      NextPage: I18n.t('open_search.pagination.next_page'),
      PreviousPage: I18n.t('open_search.pagination.previous_page'),
      OfTotal: (total) => I18n.t('open_search.pagination.of_total', {total: total}),
    },
    ResultsPerPage: {
      Label: I18n.t('open_search.results_per_page.label'),
      Unit: I18n.t('open_search.results_per_page.unit'),
    },
    Stats: {
      FoundItems: (count) => I18n.t('open_search.stats.results_found', {count: count}),
    },
    Hits: {
      NoResults: I18n.t('open_search.stats.no_results'),
      LearnMore: I18n.t('open_search.hits.learn_more'),
      Featured: I18n.t('open_search.hits.featured'),
    }
  }
}

export function providerConfig(options) {
  const config = {
    alwaysSearchOnInitialLoad: true,
    initialState: {},
    searchQuery: {
      sort: [],
      filters: [],
      result_fields: {},
      facets: {},
      disjunctiveFacets: []
    },
  };

  if (options?.filters) {
    try {
      config.searchQuery.filters = JSON.parse(options.filters);
    } catch (e) {
      console.log('Invalid filters provided');
    }
  }

  if (options?.displayOptions) {
    config.initialState.resultsPerPage = options?.displayOptions?.itemsPerPage || Config.DisplayOptions.ItemsPerPage.DefaultValue;
  }

  // Sorting
  if (options?.sorting && Array.isArray(options.sorting) && options.sorting.length > 0) {
    const firstOption = options.sorting[0];

    config.initialState.sortField = firstOption.field;
    config.initialState.sortDirection = firstOption.direction;
  }

  // Facets
  if (options?.facets && Array.isArray(options.facets)) {
    for (const facetConfig of options.facets) {
      const {field} = facetConfig;
      config.searchQuery.facets[field] = {type: 'value', size: 20, sort: "value"}
      config.searchQuery.disjunctiveFacets.push(field);
    }
  }

  return config;
}

function validateOpenSearchConfig(config) {
  if (!config?.indexes || `${config.indexes}`.trim() === '') {
    return false;
  }

  if (config?.facets && Array.isArray(config.facets)) {
    for (const facet of config.facets) {
      const {field, label} = facet;
      if (!field || !label || `${field}`.trim() === '' || `${label}`.trim() === '') {
        return false;
      }
    }
  }

  return true;
}

function OpenSearchConfigs(providers = []) {
  return providers.filter(validateOpenSearchConfig);
}

function ExtractViewMode(settings) {
  if (!settings) return null;

  // New shape: per-mode config keyed by mode; the active mode is settings.viewMode.
  if (settings.viewMode && settings.viewModeConfigs) {
    const config = settings.viewModeConfigs[settings.viewMode];
    if (config) {
      const {mapping = {}, imagePadding, clickableCard, truncatePretitle} = config;
      return {viewMode: settings.viewMode, mapping, imagePadding, clickableCard, truncatePretitle};
    }
  }

  // Transitional: flat shape { viewMode, mapping, ...flags } from pre-nested builds.
  // Remove once such pages are re-saved into the nested viewModeConfigs shape.
  if (settings.viewMode && settings.mapping) {
    const {viewMode, mapping = {}, imagePadding, clickableCard, truncatePretitle} = settings;
    return {viewMode, mapping, imagePadding, clickableCard, truncatePretitle};
  }

  // Legacy back-compat (runtime read shim): published pages still store the old per-mode
  // arrays and are not auto-migrated. Read the first entry; the new key above wins when
  // present. Remove once all live pages are re-saved into the new shape.
  if (settings.cardViewModes?.[0]) {
    return {...settings.cardViewModes[0], viewMode: Config.ViewModes.Card};
  }

  if (settings.accordionViewModes?.[0]) {
    return {...settings.accordionViewModes[0], viewMode: Config.ViewModes.Accordion};
  }

  return null;
}

export function ExtractConfig(settings) {
  const configs = [];

  for (const config of OpenSearchConfigs(settings?.openSearchProviders)) {
    configs.push({...config, displayOptions: settings.displayOptions});
  }

  if (configs.length === 0) {
    return null;
  }

  const config = configs.pop();
  const viewMode = ExtractViewMode(config);
  if (viewMode) {
    config.ViewMode = viewMode;
  }

  if (settings?.compactMode) {
    config.compactMode = settings.compactMode;
  }

  return config;
}

export function uniqueTotalItems(rawResponse) {
  return rawResponse?.aggregations?.unique_count?.total_unique_count?.value || 0;
}

export function connectorConfig(host, indexes, aggregation) {
  const config = {
    host: host,
    index: indexes,
  };

  config.connectionOptions = {
    headers: {
      'X-CSRF-Token': getCsrfToken(),
    },
  };

  if (aggregation && `${aggregation}`.trim() !== '') {
    config.connectionOptions.headers['x-dedup-field'] = aggregation;
  }

  const previewTokenEl = document.getElementById('library-preview-token');
  if (previewTokenEl) {
    const token = previewTokenEl.dataset?.value;
    if (token) {
      config.connectionOptions.headers['x-preview-token'] = token;
    }
  }

  return config;
}

export function useClickOutside(ref, handler) {
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        handler(event.target);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref, handler]);
}

export function hiddenFacets(facets) {
  if (!facets || !Array.isArray(facets)) return [];

  return facets.filter(item => item.hidden).map(item => item.field);
}
